import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { formatCurrencyINR } from '../../../utils/currency';

const AssetClassDistributionChart = ({ investments }) => {
  const chartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];

    const currentYear = new Date().getFullYear();
    
    // Calculate total annual investment by asset class
    const byClass = {};
    
    investments.forEach(inv => {
      if (!inv.isActive) return;
      if (inv.startYear > currentYear || (inv.endYear && inv.endYear < currentYear)) return;
      
      const type = inv.type || 'Other';
      const freq = inv.frequency || 'monthly';
      const amount = inv.amount || 0;
      let annualAmount = 0;
      
      if (freq === 'monthly') annualAmount = amount * 12;
      else if (freq === 'quarterly') annualAmount = amount * 4;
      else if (freq === 'annually') annualAmount = amount;
      else if (freq === 'one-time') annualAmount = currentYear === inv.startYear ? amount : 0;
      
      if (!byClass[type]) {
        byClass[type] = 0;
      }
      byClass[type] += annualAmount;
    });

    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
      '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
    ];

    return Object.entries(byClass)
      .map(([name, value], index) => ({
        name,
        value: Math.round(value),
        color: colors[index % colors.length]
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [investments]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground mb-1">{data.name}</p>
          <p className="text-sm text-foreground">
            {formatCurrencyINR(data.value)} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  if (!investments || investments.length === 0 || chartData.length === 0) {
    return (
      <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          <p className="text-muted-foreground">No investment data available</p>
          <p className="text-sm text-muted-foreground">Add investments to see distribution</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AssetClassDistributionChart;



